import { useState, useContext, useEffect, useMemo } from "react";
import SectionHeader from "../../shared/SectionHeader";
import TablePagination from "../../shared/TablePagination";
import { AppContext } from "./../../../../../context/app";
import { useGetUserDocumentsQuery } from "../../../../../apis/userApi.hooks";
import {
  flexRender,
  getCoreRowModel,
  ColumnFiltersState,
  getPaginationRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Plus, Pen } from "lucide-react";
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@pushpress/pushpress-ui";
import { EMPTY_VALUE, PAGINATION_PAGE_SIZE } from "../../../constants";
import ErrorState from "../../shared/ErrorState";
import { useUserContext } from "../../../hooks/useUserContext";
import TableSkeleton from "../../shared/TableSkeleton";
import { convertDateStringOrTimestampToUsDateFormat } from "../../../helpers";
import AddDocumentsModal from "./AddDocumentsModal";
import DocumentsActionMenu from "./DocumentsActionsMenu";
type DocumentsProps = {
  userUuid: string;
};

export default function Documents({ userUuid }: DocumentsProps) {
  const { client } = useContext(AppContext);
  const { setHasUnsignedDocuments } = useUserContext();
  const { data, isLoading, isError, refetch } = useGetUserDocumentsQuery(
    userUuid,
    client.uuid
  );

  const [showAddDocuments, setShowAddDocuments] = useState<boolean>(false);

  const defaultActiveFilter = [
    {
      id: "active",
      value: true,
    },
    {
      id: "signed",
      value: true,
    },
  ];

  type ViewModeType = "signed" | "unsigned" | "revoked";

  const [columnFilters, setColumnFilters] =
    useState<ColumnFiltersState>(defaultActiveFilter);
  const [viewMode, setViewMode] = useState<ViewModeType>("signed");

  const setFilters = (status: ViewModeType) => {
    switch (status) {
      case "unsigned":
        setColumnFilters([
          {
            id: "active",
            value: true,
          },
          {
            id: "signed",
            value: false,
          },
        ]);
        break;
      case "signed":
        setColumnFilters(defaultActiveFilter);
        break;
      case "revoked":
        setColumnFilters([
          {
            id: "active",
            value: false,
          },
        ]);
        break;
      default:
        setColumnFilters(defaultActiveFilter);
        break;
    }
    setViewMode(status);
  };

  const tableData = useMemo(() => data ?? [], [data]);
  const columns = [
    {
      header: "Document",
      accessorKey: "document",
      accessorFn: (row) => row?.doc?.title,
    },
    {
      header: "Version",
      accessorKey: "version",
      accessorFn: (row) => row?.doc?.version,
    },
    {
      header: "Created",
      accessorKey: "createdOn",
      accessorFn: (row) =>
        convertDateStringOrTimestampToUsDateFormat(row.create_timestamp),
    },
    {
      header: "Signed",
      accessorKey: "signed",
      accessorFn: (row) =>
        row.is_signed
          ? convertDateStringOrTimestampToUsDateFormat(row.signed_timestamp)
          : "Unsigned",
      filterFn: (row, columnId, filterValue) => {
        return !!row.original.is_signed === filterValue;
      },
    },
    {
      header: "Active",
      accessorKey: "active",
      cell: ({ row: { original } }) => {
        if (original.active) {
          return <Badge variant="success">Active</Badge>;
        }
        return <Badge variant="secondary">Revoked</Badge>;
      },
    },
    {
      id: "actions",
      accessorKey: "actions",
      header: null,
      cell: ({ row: { original } }) => {
        if (!original.active) return EMPTY_VALUE;
        return <DocumentsActionMenu userUuid={userUuid} document={original} />;
      },
    },
  ];

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      columnFilters,
    },
    initialState: {
      pagination: {
        pageSize: PAGINATION_PAGE_SIZE,
      },
    },
    getFilteredRowModel: getFilteredRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const showPagination = !isLoading && tableData.length > PAGINATION_PAGE_SIZE;
  const hasUnsignedDocuments = useMemo(
    () => tableData.some((document) => !document.is_signed && document.active),
    [tableData]
  );

  useEffect(() => {
    setHasUnsignedDocuments(hasUnsignedDocuments);
  }, [hasUnsignedDocuments, setHasUnsignedDocuments]);

  return (
    <div className="tw-space-y-4">
      <SectionHeader
        title="Documents"
        actions={
          <div className="tw-flex tw-items-center tw-gap-4">
            <Button
              disabled={!hasUnsignedDocuments}
              variant="outline"
              onClick={() =>
                window.open(
                  `/open/documents/${userUuid}`,
                  "_blank",
                  "noopener,noreferrer"
                )
              }
              size="sm"
            >
              <Pen className="tw-h-3 tw-w-3 tw-mr-1" /> Sign all unsigned
              documents
            </Button>
            <Button onClick={() => setShowAddDocuments(true)} size="sm">
              <Plus className="tw-mr-1 tw-h-4 tw-w-4" />
              Add Document
            </Button>
          </div>
        }
      />
      <AddDocumentsModal
        clientUuid={client.uuid}
        userUuid={userUuid}
        showAddDocuments={showAddDocuments}
        setShowAddDocuments={setShowAddDocuments}
        onConfirmation={() => setFilters("unsigned")}
      />
      <div className="tw-rounded-lg tw-border tw-p-4 tw-bg-white tw-flex tw-flex-col tw-gap-4">
        <Tabs
          value={viewMode}
          onValueChange={(value: ViewModeType) => setFilters(value)}
        >
          <TabsList className="tw-h-9">
            <TabsTrigger value="signed" className="tw-text-xs">
              Signed
            </TabsTrigger>
            <TabsTrigger value="unsigned" className="tw-text-xs">
              Unsigned
            </TabsTrigger>
            <TabsTrigger value="revoked" className="tw-text-xs">
              Revoked
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="tw-rounded tw-ring-1 tw-ring-black/5">
          {isError ? (
            <ErrorState refetch={refetch} />
          ) : isLoading ? (
            <TableSkeleton
              numCols={columns.length}
              numRows={PAGINATION_PAGE_SIZE}
            />
          ) : (
            <Table className="tw-divide-y tw-divide-neutral-300">
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id} className="tw-bg-neutral-50">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody className="tw-divide-y tw-divide-neutral-200 tw-bg-white">
                {table.getRowModel()?.rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id} className="tw-py-3">
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <TableCell key={cell.id} className="tw-h-14">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="tw-h-24 tw-text-center"
                    >
                      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-8 tw-text-center">
                        <h4 className="tw-mb-2">
                          No {viewMode} documents for this user
                        </h4>
                        <p className="tw-text-neutral-500 tw-mb-6">
                          Add a document to get started
                        </p>
                        <div className="tw-flex tw-gap-4">
                          <Button
                            onClick={() => setShowAddDocuments(true)}
                            size="sm"
                          >
                            <Plus className="tw-mr-1 tw-h-4 tw-w-4" />
                            Add Document
                          </Button>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
        {showPagination && <TablePagination table={table} />}
      </div>
    </div>
  );
}
